import React from "react";
import { styled } from "../stitches.config";
import { VehicleCardElement } from "../components/Article/Parts/Slate/types";
import AuthorBlock from './AuthorBlock';
import Media from "../components/common/Media";
import useMediaQuery from "../helpers/useMediaQuery";
import VehicleCardBackground from "./assets/images/karta-pojazdu.png";

import { ThemeWrapper } from "./components/StitchesThemeProvider";
import NoSSR from "../helpers/NoSSR";

type VehicleCardProps = {
  element: VehicleCardElement;
  article: ArticleAuthor;
};

const VehicleCard: React.FC<VehicleCardProps> = ({
  element,
  article,
  ...props
}) => {
  const {
    url, carModel, carYearOfProduction, carType, carWeight, carScale, carGearbox, carEngineType, carVolume, carTorque, carPrice, carNumberOfSeats, carTrunkCapacity,
  } = element;
  const isDesktop = useMediaQuery('bp3');
  return (
    <StyledVehicleCard className="vehicle-card" colorMode="light">
      <Title>Karta pojazdu</Title>
      <MainWrapper>
        <MainBlock>
          <AuthorBlock
            author={article.author}
            border={true}
            articleDate={article.date}
            articleUpdateDate={article.update_date}
            articleReadingTime={article.reading_time}
            starButton
          />
          <Label className="label">Wystawiający recenzję</Label>
        </MainBlock>
        <MainBlock>
          <MediaWrapper>
            <NoSSR fallback={<MediaPlaceholder />}>
              <Media src={url} width={"200px"} height={isDesktop ? "120px" : "234px"} disablePreload={true} />
            </NoSSR>
          </MediaWrapper>
          <Label className="label">Zdjęcie pojazdu </Label>
        </MainBlock>
      </MainWrapper>
      <BlockWrapper>
        <Block className="vehiclecard-max-width vehiclecard-right-border">
          <p>{carModel}</p>
          <Label>Marka, typ, model pojazdu</Label>
        </Block>
        <Block>
          <p>{carYearOfProduction}</p>
          <Label>Rok produkcji</Label>
        </Block>
      </BlockWrapper>
      <BlockWrapper>
        <Block className="vehiclecard-max-width vehiclecard-right-border">
          <p>{carType}</p>
          <Label>Rodzaj pojazdu i typ nadwozia</Label>
        </Block>
        <Block>
          <p>{carWeight}</p>
          <Label>Masa pojazdu</Label>
        </Block>
      </BlockWrapper>
      <BlockWrapper>
        <Block className="vehiclecard-max-width vehiclecard-right-border">
          <p>{carScale}</p>
          <Label>Wymiary pojazdu</Label>
        </Block>
        <Block>
          <p>{carGearbox}</p>
          <Label>Skrzynia biegów</Label>
        </Block>
      </BlockWrapper>
      <BlockWrapper>
        <Block className="vehiclecard-max-width vehiclecard-right-border">
          <p>{carEngineType}</p>
          <Label>Rodzaj silnika</Label>
        </Block>
        <Block className="vehiclecard-mid-width vehiclecard-right-border">
          <p>{carVolume}</p>
          <Label>Pojemność / Moc silnika</Label>
        </Block>
        <Block >
          <p>{carTorque}</p>
          <Label>Moment obrotowy</Label>
        </Block>
      </BlockWrapper>
      <BlockWrapper>
        <Block className="vehiclecard-max-width vehiclecard-right-border">
          <p>{carPrice}</p>
          <Label>Cena bazowa pojazdu / Cena testowanego modelu</Label>
        </Block>
        <Block className="vehiclecard-mid-width vehiclecard-right-border">
          <p>{carNumberOfSeats}</p>
          <Label>Liczba miejsc</Label>
        </Block>
        <Block>
          <p>{carTrunkCapacity}</p>
          <Label>Pojemność bagażnika</Label>
        </Block>
      </BlockWrapper>
    </StyledVehicleCard>
  );
};

const MediaPlaceholder = styled("div", {
  height: "234px",
  width: "100%",
  "@bp3": {
    height: "120px",
    maxWidth: "200px",
  }
});

const MediaWrapper = styled('div', {
  width: '100%',
  height: '100%',
  img: {
    maxWidth: "100%",
    "@bp3": {
      maxWidth: "200px",
    }
  }
})

const StyledVehicleCard = styled(ThemeWrapper, {
  width: "100%",
  position: 'relative',
  zIndex: 2,
  "& > div": {
    position: 'relative',
  },
  "&::before": {
    content: '',
    position: 'absolute',
    zIndex: 0,
    width: '106%',
    height: '106%',
    top: '-3%',
    left: '-3%',
    backgroundImage: `url("${VehicleCardBackground}")`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',

  }
});

const Title = styled("div", {
  marginTop: "24px",
  width: "100%",
  fontSize: "18px",
  lineHeight: "22px",
  textAlign: "center",
  marginBottom: "40px",
  fontWeight: 700,
  color: "$grayscale100",
  "@bp4": {
    fontSize: "32px",
    lineHeight: "36px",
  }
});

const MainWrapper = styled("div", {
  "@bp3": {
    display: "flex",
    justifyContent: "space-between",
  },
});

const BlockWrapper = styled("div", {
  display: "flex",
  borderTop: "1px solid $grayscale100",
  "@bp3": {
    borderTop: "2px solid $grayscale100",
  },
  "@bp4": {
    borderTop: "3px solid $grayscale100",
  },
  "& .vehiclecard-max-width": {
    width: "100%",
    padding: "4px 4px 4px 0px",

    "@bp3": {
      padding: "10px 10px 10px 0px",
    },
  },
  "& .vehiclecard-mid-width": {
    minWidth: "100px !important",
    "@bp3": {
      minWidth: "225px !important"
    },
  },
  "& .vehiclecard-right-border": {
    borderRight: "1px solid $grayscale100",
    "@bp3": {
      borderRight: "2px solid $grayscale100",
    },
    "@bp4": {
      borderRight: "3px solid $grayscale100",
    }
  }
});

const Block = styled("div", {
  padding: "4px",
  color: "$grayscale0 !important",
  minWidth: "100px",

  "@bp3": {
    minWidth: "200px",
    padding: "10px",
  },

  p: {
    margin: 0,
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "18px",
    color: "$grayscale100 !important",
    "@bp3": {
      fontSize: "18px",
      lineHeight: "22px",
    },
    "@bp4": {
      fontSize: "32px",
      lineHeight: "36px",
      marginBottom: "8px",
      fontWeight: 700,
    }
  }
});

const MainBlock = styled("div", {
  padding: "0px 0px 4px 0px",
  minWidth: "200px",
  "@bp3": {
    padding: "0px 0px 40px 0px",
    display: "flex",
    position: "relative",
    alignItems: "center",
  },

  "& .label": {
    marginTop: "4px",
    "@bp3": {
      marginTop: "8px",
      position: "absolute",
      bottom: "10px",
      left: 0,
    }
  },

  "& .avatar": {
    width: "72px",
    height: "72px",
    borderRadius: "72px",
    marginRight: "20px !important",
  },
});

const Label = styled("span", {
  margin: 0,
  display: "block",
  color: "$grayscale100 !important",
  fontSize: "8px",
  lineHeight: "12px",
  "@bp3": {
    fontSize: "14px",
    lineHeight: "18px",
  }
});


export default VehicleCard;
